import { useEffect } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Label,
  Host,
  useSelectedHost,
  DEFAULT_HOSTS,
} from "@tellescope/ui";
import { useAuth } from "@tellescope/tanstack-query";
import { useTranslation } from "react-i18next";

interface HostSelectorProps {
  className?: string;
  onHostChange?: (host: Host) => void;
}

export function HostSelector({ className, onHostChange }: HostSelectorProps) {
  const { t } = useTranslation("auth");
  const [selectedHost, setSelectedHost] = useSelectedHost();
  const { session, resetSession } = useAuth();

  useEffect(() => {
    // Dispatch event to notify other components about the initial host
    window.dispatchEvent(
      new CustomEvent("tellescope-host-changed", {
        detail: session.host,
      })
    );
  }, [session.host]);

  const handleHostChange = (value: string) => {
    const host = DEFAULT_HOSTS.find((h) => h.url === value);
    if (host) {
      setSelectedHost(host);

      // Reinitialize session with new host
      resetSession({ host: host.url });

      // Dispatch custom event to notify other components (like DevApiIndicator)
      window.dispatchEvent(
        new CustomEvent("tellescope-host-changed", {
          detail: host,
        })
      );

      onHostChange?.(host);
    }
  };

  return (
    <div className={className}>
      <Label htmlFor="host-selector" className="text-xs text-muted-foreground">
        {t("Development Host")}
      </Label>
      <Select value={selectedHost.url} onValueChange={handleHostChange}>
        <SelectTrigger size="sm" className="mt-1">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {DEFAULT_HOSTS.map((host) => (
            <SelectItem key={host.url} value={host.url}>
              <div className="flex items-center gap-2">
                <div
                  className={`w-2 h-2 rounded-full ${
                    host.name === "Local" ? "bg-blue-500" : host.name === "Staging" ? "bg-yellow-500" : "bg-green-500"
                  }`}
                />
                <span>{host.name}</span>
                <span className="text-xs text-muted-foreground">{host.url}</span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
